/**
 * Centralized routing rule expression building logic
 * This eliminates duplication between RoutingRuleEditor and routingRuleBuilder
 */

// ====================== TYPES ======================

export interface RouteTarget {
  type: "agent" | "action" | "llm" | "none";
  agent_id?: number;
  action_id?: number;
}

export interface RoutingRuleCondition {
  target_type: "request" | "state";
  variable_path: string;
  operator: "equals" | "not_equals" | "gt" | "gte" | "lt" | "lte" | "like" | "matches" | "sem_match" | "sem_similarity" | "classify" | "not_classify" | "sentiment" | "is_null" | "is_not_null";
  value_text?: string;
  value_number?: number;
  value_bool?: boolean;
  comparison_op?: ">" | ">=" | "=" | "<=" | "<" | "IS" | "IS_NOT";
  threshold_number?: number;
  logical_op?: "AND" | "OR";
  group_start?: number;
  group_end?: number;
  enabled?: boolean;
}

export interface RoutingRule {
  id?: string | number;
  label?: string;
  if: string;
  condition_expression_template: string;
  conditions: RoutingRuleCondition[];
  route: RouteTarget;
  enabled?: boolean;
}

// ====================== OPERATOR MAPPING ======================

/**
 * Maps UI operators to backend operators
 * This is the single source of truth for operator mapping
 */
export const mapOperatorToBackend = (operator: string): string => {
  const operatorMap: Record<string, string> = {
    "==": "equals",
    "!=": "not_equals",
    ">": "gt",
    ">=": "gte",
    "<": "lt",
    "<=": "lte",
    "like": "like",
    "matches": "matches",
    "sem_match": "sem_match",
    "sem_similarity": "sem_similarity",
    "classify": "classify",
    "not_classify": "not_classify",
    "sentiment": "sentiment",
    "is_null": "is_null",
    "is_not_null": "is_not_null"
  };
  return operatorMap[operator] || operator;
};

/**
 * Maps backend operators to expression operators
 * This is the single source of truth for expression operator mapping
 */
export const mapOperatorToExpression = (operator: string): string => {
  const operatorMap: Record<string, string> = {
    equals: "==",
    not_equals: "!=",
    gt: ">",
    gte: ">=",
    lt: "<",
    lte: "<=",
    like: ".like",
    matches: ".matches",
    sem_match: ".sem_match",
    sem_similarity: ".sem_similarity",
    classify: ".classify",
    not_classify: ".not_classify",
    sentiment: ".sentiment",
    is_null: "== null",
    is_not_null: "!= null",
  };
  return operatorMap[operator] || operator;
};

// ====================== EXPRESSION BUILDING ======================

/**
 * Builds a condition expression string from a single condition
 * This is the core logic for converting conditions to expressions
 */
export function buildConditionExpression(condition: RoutingRuleCondition): string {
  const targetPrefix = condition.target_type === "state" ? "state." : "request.";
  const varPath = `${targetPrefix}${condition.variable_path}`;

  if (condition.operator === "is_null") {
    return `${varPath} == null`;
  } else if (condition.operator === "is_not_null") {
    return `${varPath} != null`;
  } else if (["like", "matches", "sem_match", "sem_similarity", "classify", "not_classify", "sentiment"].includes(condition.operator)) {
    // Method-style operators
    let value: string;
    if (condition.value_text !== undefined && condition.value_text !== null) {
      value = `"${condition.value_text}"`;
    } else if (condition.value_number !== undefined && condition.value_number !== null) {
      value = String(condition.value_number);
    } else if (condition.value_bool !== undefined && condition.value_bool !== null) {
      value = condition.value_bool ? "true" : "false";
    } else {
      value = "\"\"";
    }

    const op = mapOperatorToExpression(condition.operator);

    if (condition.operator === "sem_similarity" && condition.threshold_number !== undefined) {
      const compOp = condition.comparison_op || ">=";
      return `${varPath}${op}(${value}) ${compOp} ${condition.threshold_number}`;
    } else if (condition.operator === "sentiment" && condition.comparison_op) {
      return `${varPath}${op}(${value}, "${condition.comparison_op}")`;
    } else {
      return `${varPath}${op}(${value})`;
    }
  } else {
    // Standard comparison operators
    let value: string;
    if (condition.value_text !== undefined && condition.value_text !== null) {
      value = `"${condition.value_text}"`;
    } else if (condition.value_number !== undefined && condition.value_number !== null) {
      value = String(condition.value_number);
    } else if (condition.value_bool !== undefined && condition.value_bool !== null) {
      value = condition.value_bool ? "true" : "false";
    } else {
      value = "null";
    }

    const op = mapOperatorToExpression(condition.operator);
    return `${varPath} ${op} ${value}`;
  }
}

/**
 * Builds the condition expression template and full expression from conditions
 * This is the main function that both RoutingRuleEditor and routingRuleBuilder should use
 */
export function buildRoutingRuleExpressions(conditions: RoutingRuleCondition[]): {
  template: string;
  expression: string;
} {
  if (!conditions || conditions.length === 0) {
    return { template: "", expression: "" };
  }

  const templateParts: string[] = [];
  const expressionParts: string[] = [];

  conditions.forEach((cond, index) => {
    // Add opening parentheses BEFORE the condition
    const groupStart = cond.group_start || 0;
    for (let i = 0; i < groupStart; i++) {
      templateParts.push("(");
      expressionParts.push("(");
    }

    // Add condition placeholder for template
    templateParts.push(`{${index}}`);

    // Build the actual condition expression
    const conditionStr = buildConditionExpression(cond);
    expressionParts.push(conditionStr);

    // Add closing parentheses AFTER the condition
    const groupEnd = cond.group_end || 0;
    for (let i = 0; i < groupEnd; i++) {
      templateParts.push(")");
      expressionParts.push(")");
    }

    // Add logical operator between conditions (not for last condition)
    // The logical_op of the NEXT condition represents the operator before it (between current and next)
    if (index < conditions.length - 1) {
      const nextCondition = conditions[index + 1];
      const logicalOp = nextCondition.logical_op || "AND";
      templateParts.push(` ${logicalOp} `);
      expressionParts.push(` ${logicalOp} `);
    }
  });

  return {
    template: templateParts.join(""),
    expression: expressionParts.join(""),
  };
}

// ====================== RULE PREPARATION ======================

/**
 * Prepares a routing rule for submission to RoutingService.createRule()
 * Takes the assistant's rule spec and builds the complete rule object
 */
export function prepareRoutingRuleForSubmission(
  assistantRule: {
    agent_id?: number;
    label?: string;
    route: RouteTarget;
    conditions: RoutingRuleCondition[];
    enabled?: boolean;
  }
): RoutingRule {
  const { template, expression } = buildRoutingRuleExpressions(assistantRule.conditions);

  return {
    label: assistantRule.label || "New Rule",
    if: expression,
    condition_expression_template: template,
    conditions: assistantRule.conditions,
    route: assistantRule.route,
    enabled: assistantRule.enabled !== false,
  };
}

// ====================== CONDITION CONVERSION ======================

/**
 * Converts a UI condition to backend format
 * This handles the complex logic for mapping UI condition values to backend fields
 */
export function convertConditionToBackend(
  condition: {
    variable: string;
    operator: string;
    value: string;
    comparisonOp?: string;
    comparedValue?: string;
  },
  logicalOp?: string
): RoutingRuleCondition {
  // Determine target_type and variable_path
  let target_type: "request" | "state";
  let variable_path: string;
  
  if (condition.variable.startsWith("request.")) {
    target_type = "request";
    variable_path = condition.variable.replace("request.", "");
  } else if (condition.variable.startsWith("state.")) {
    target_type = "state";
    variable_path = condition.variable.replace("state.", "");
  } else {
    // Default to request for backward compatibility
    target_type = "request";
    variable_path = condition.variable;
  }

  const backendOperator = mapOperatorToBackend(condition.operator);

  return {
    target_type,
    variable_path,
    operator: backendOperator as any,
    value_text: (condition.operator === "is_null" || condition.operator === "is_not_null") ? undefined : 
      ["sem_match", "sem_similarity", "like", "matches", "classify", "not_classify", "sentiment"].includes(condition.operator) ? condition.value : 
      (condition.operator === "==" || condition.operator === "!=") ? condition.value : undefined,
    value_number: (condition.operator === "is_null" || condition.operator === "is_not_null") ? undefined : 
      [">", ">=", "<", "<="].includes(condition.operator) ? parseFloat(condition.value) : undefined,
    value_bool: (condition.operator === "is_null" || condition.operator === "is_not_null") ? undefined : 
      (condition.value === "true" || condition.value === "false" || condition.value === "null") ? 
        (condition.value === "null" ? null : condition.value === "true") : undefined,
    comparison_op: condition.operator === "sem_similarity" ? (condition.comparisonOp || ">") as any : 
      condition.operator === "sentiment" ? (condition.comparisonOp || "IS") as any : 
      (condition.comparisonOp as any || undefined),
    threshold_number: condition.operator === "sem_similarity" ? parseFloat(condition.comparedValue || "0.85") : 
      (condition.comparisonOp ? parseFloat(condition.comparedValue || "0") : undefined),
    enabled: true,
    logical_op: logicalOp as any,
    group_start: 0,
    group_end: 0,
  };
}
